/**
 * Phone bottom sheet (docs/mobile-design.md §2): grabber and title; Escape, the backdrop and a swipe down close it.
 * t300u: no X in the corner (every sheet swipes down or closes on the backdrop); a Close button stays for screen readers
 * and keyboards only. Sheets that need one keep their own Done / Save action.
 * t189u: rendered into document.body, so a sheet opened from inside another sheet (a comment's actions over the
 * comments sheet) is positioned against the screen, not clipped inside the lower sheet; closing it leaves the lower one.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { keyboardBox, useKeepFocusedVisible, useKeyboardInset } from './keyboard';

/** Open sheets, newest last: Escape closes only the top one (a picker over the shift sheet). */
const stack: object[] = [];
/** How far a downward drag (from the top of the sheet's scroll) must go to close it. */
export const SWIPE_CLOSE_PX = 80;

/**
 * t202u: `footer` (a comment composer) is pinned under a scrolling body and rides on the on-screen keyboard (the sheet
 * follows the visible area, like the chat composer); `scrollToEnd` opens the body at its end (the latest comments).
 */
export function BottomSheet({ open, onClose, title, children, testId, footer, scrollToEnd }: { open: boolean; onClose: () => void; title: string; children: ReactNode; testId?: string; footer?: ReactNode; scrollToEnd?: boolean }) {
  // t207u: every sheet follows the visible area while the keyboard is up (not just composer sheets).
  const kb = useKeyboardInset();
  const body = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  useKeepFocusedVisible(dialog, kb, open);
  // Keep the end in view while the comments load (until the person scrolls or touches the list).
  useEffect(() => {
    if (!open || !scrollToEnd) return;
    const el = body.current; if (!el) return;
    let stop = false;
    const end = () => { if (!stop) el.scrollTop = el.scrollHeight; };
    const off = () => { stop = true; };
    end();
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(end);
    mo?.observe(el, { childList: true, subtree: true });
    const t = setTimeout(() => { stop = true; mo?.disconnect(); }, 2500);
    el.addEventListener('touchstart', off, { passive: true }); el.addEventListener('wheel', off, { passive: true });
    return () => { clearTimeout(t); mo?.disconnect(); el.removeEventListener('touchstart', off); el.removeEventListener('wheel', off); };
  }, [open, scrollToEnd]);
  const close = useRef(onClose);
  close.current = onClose;
  const drag = useRef<{ y: number; x: number; active: boolean } | null>(null);
  const [dy, setDy] = useState(0);
  // t537u: a sheet opened over another one paints above it, even when its portal was mounted first (a sheet kept mounted inside the lower one).
  const [depth, setDepth] = useState(0);
  useEffect(() => {
    if (!open) return;
    const me = {};
    stack.push(me);
    setDepth(stack.length);
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && stack[stack.length - 1] === me) { e.stopImmediatePropagation(); close.current(); } };
    window.addEventListener('keydown', k);
    return () => { window.removeEventListener('keydown', k); stack.splice(stack.indexOf(me), 1); };
  }, [open]);
  useEffect(() => { if (!open) setDy(0); }, [open]);
  // Touches from a sheet stacked on this one reach these handlers through React (portal bubbling): only our own DOM counts.
  const mine = (e: React.TouchEvent) => e.currentTarget.contains(e.target as Node);
  const scrollTop = (dialog: HTMLElement) => (footer ? body.current?.scrollTop ?? 0 : dialog.scrollTop);
  // t300u: a list scrolled inside the sheet (the filter sheet's columns) scrolls back up before a drag moves the sheet.
  const atTop = (e: React.TouchEvent<HTMLElement>) => scrollTop(e.currentTarget) <= 0 && !innerScrolled(e.target as Node, e.currentTarget);
  const sheet = (
    <div className={clsx('fixed inset-0 z-50', !open && 'pointer-events-none')} aria-hidden={!open} data-sheet-root
      style={open ? { ...keyboardBox(kb), ...(depth > 1 ? { zIndex: 50 + depth } : null) } : undefined}>
      <div className={clsx('absolute inset-0 bg-black/50 transition-opacity', open ? 'opacity-100' : 'opacity-0')} onClick={onClose} data-testid={testId ? `${testId}-backdrop` : undefined} />
      <div ref={dialog} role="dialog" aria-modal="true" aria-label={title} data-testid={testId}
        onTouchStart={e => { if (!mine(e)) return; const t = e.touches[0]!; drag.current = { y: t.clientY, x: t.clientX, active: atTop(e) }; }}
        onTouchMove={e => {
          const d = drag.current; if (!d || !d.active || !mine(e)) return;
          const t = e.touches[0]!; const down = t.clientY - d.y;
          if (down > 0 && down > Math.abs(t.clientX - d.x) && atTop(e)) setDy(down);
          else if (dy) setDy(0);
        }}
        onTouchEnd={() => { const far = dy >= SWIPE_CLOSE_PX; drag.current = null; setDy(0); if (far) onClose(); }}
        onTouchCancel={() => { drag.current = null; setDy(0); }}
        style={dy ? { transform: `translateY(${dy}px)`, transition: 'none' } : undefined}
        className={clsx('absolute inset-x-0 bottom-0 rounded-t-2xl bg-white shadow-2xl transition-transform duration-150 dark:bg-slate-900',
          kb.inset ? 'max-h-full' : 'max-h-[85vh]',
          footer ? 'flex flex-col overflow-hidden' : clsx('overflow-y-auto overscroll-contain', kb.inset ? 'pb-2' : 'pb-[calc(env(safe-area-inset-bottom)+8px)]'),
          // Open = no transform at all: a transformed ancestor breaks position:fixed children on iOS.
          open ? 'transform-none' : 'invisible translate-y-full')}>
        <div className={clsx('z-[2] bg-white dark:bg-slate-900', footer ? 'shrink-0' : 'sticky top-0')}>
          <div className="flex justify-center pt-2"><span className="h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-700" aria-hidden /></div>
          <div className="flex min-h-[44px] items-center px-4">
            <h2 className="min-w-0 flex-1 truncate text-base font-semibold">{title}</h2>
            <button type="button" onClick={onClose} aria-label="Close" data-testid={testId ? `${testId}-close` : undefined} className="sr-only">Close</button>
          </div>
        </div>
        {footer ? <>
          <div ref={body} className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid={testId ? `${testId}-body` : undefined}>{children}</div>
          <div className={clsx('shrink-0 border-t border-slate-200 bg-white px-3 pt-2 dark:border-slate-800 dark:bg-slate-900', kb.inset ? 'pb-2' : 'pb-[calc(env(safe-area-inset-bottom)+8px)]')} data-testid={testId ? `${testId}-footer` : undefined}>{footer}</div>
        </> : children}
      </div>
    </div>
  );
  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
}

/** True when an element between the touch and the sheet is scrolled down (it should scroll, not drag the sheet). */
export function innerScrolled(from: Node | null, sheet: HTMLElement): boolean {
  for (let el = from instanceof HTMLElement ? from : from?.parentElement ?? null; el && el !== sheet; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return false;
}
